import moment from 'moment';

/**
 * Calculate maintenance status based on schedule start date and frequency
 * @param {Object} asset - Asset object containing maintenance data
 * @param {number} currentTime - Current timestamp in seconds
 * @returns {Object} Maintenance status object with label, value, and asset_page_label
 */
export function calculateMaintenanceStatus(asset, currentTime = moment().unix()) {
    // Validate required fields
    if (!asset.maintenance_frequency || !asset.maintenance_schedule_start_date) {
        return { asset_page_label: '' };
    }

    const scheduleStartTime = asset.maintenance_schedule_start_date.seconds;
    const currentDay = moment.utc(currentTime * 1000);

    if (currentTime < scheduleStartTime) {
        return {
            label: 'Maintenance Schedule Not Started',
            value: '#2196F3', // Blue color for "not started yet"
            asset_page_label: 'Not Started'
        };
    } else {

        // Calculate how many maintenance cycles have passed since the start date
        const timeSinceStart = currentTime - scheduleStartTime;
        const cyclesPassed = Math.floor(timeSinceStart / asset.maintenance_frequency.value);

        // Calculate the last maintenance due date (the most recent one that should have been completed)
        const lastMaintenanceDueTime = scheduleStartTime + (cyclesPassed * asset.maintenance_frequency.value);

        // Calculate the next maintenance due date (always based on schedule, not actual maintenance date)
        const nextMaintenanceDueTime = scheduleStartTime + ((cyclesPassed + 1) * asset.maintenance_frequency.value);

        // Adjust the due time to 23:30 of the due date for better UX
        const maintenanceDueDate = moment.utc(nextMaintenanceDueTime * 1000);
        const adjustedDueTime = maintenanceDueDate.clone().hour(23).minute(30).second(0).unix();

        const maintenanceDaysLeft = maintenanceDueDate.diff(currentDay, 'days');

        // Check if maintenance is overdue by comparing last_maintained_date with last due date
        const lastMaintainedTime = asset.last_maintained_date ? asset.last_maintained_date.seconds : 0;
        const isOverdue = lastMaintainedTime < lastMaintenanceDueTime;

        // Determine status based on current time vs adjusted due time and maintenance history
        if (currentTime >= adjustedDueTime || isOverdue) {
            return {
                label: 'Maintenance Overdue',
                value: '#f44336',
                asset_page_label: 'Overdue'
            };
        } else if ((adjustedDueTime - asset.maintenance_frequency.threshold) <= currentTime) {
            // Amber status - maintenance is due soon
            const label = asset.maintenance_frequency.value === 86400
                ? 'Maintenance Required in 1 day.'
                : `Maintenance Required in ${maintenanceDaysLeft + 1} day(s).`;

            return {
                label,
                value: '#ffbf00',
                asset_page_label: 'Warning'
            };
        } else {
            // Green status - maintenance is not due yet
            const label = asset.maintenance_frequency.value === 86400
                ? 'Maintenance Required in 1 day.'
                : `Maintenance Required in ${maintenanceDaysLeft + 1} day(s).`;

            return {
                label,
                value: '#439446',
                asset_page_label: 'OK'
            };
        }
    }
}

/**
 * Calculate warranty status based on expiry date and warning threshold
 * @param {Object} asset - Asset object containing warranty data
 * @param {number} currentTime - Current timestamp in seconds
 * @returns {Object} Warranty status object with label, value, and asset_page_label
 */
export function calculateWarrantyStatus(asset, currentTime = moment().unix()) {
    if (!asset.warranty_expiry_date) {
        return { asset_page_label: '' };
    }

    const warrantyDueDate = moment.utc(asset.warranty_expiry_date.seconds * 1000);
    const currentDay = moment.utc(currentTime * 1000);
    const warrantyDaysLeft = warrantyDueDate.diff(currentDay, 'days') + 1;
    const warrantyWarningValue = asset.warranty_warning && asset.warranty_warning.value
        ? asset.warranty_warning.value
        : 0;

    if (currentTime > warrantyDueDate.unix()) {
        return {
            label: 'Warranty Expired',
            value: '#f44336',
            asset_page_label: 'Out of Warranty'
        };
    } else if ((warrantyDueDate.unix() - warrantyWarningValue) < currentTime) {
        return {
            label: `Warranty Expires in ${warrantyDaysLeft} day(s).`,
            value: '#ffbf00',
            asset_page_label: 'Warning'
        };
    } else {
        return {
            label: `Warranty Expires in ${warrantyDaysLeft} day(s).`,
            value: '#439446',
            asset_page_label: 'In Warranty'
        };
    }
}

/**
 * Calculate lifecycle status based on expiry date and warning threshold
 * @param {Object} asset - Asset object containing lifecycle data
 * @param {number} currentTime - Current timestamp in seconds
 * @returns {Object} Lifecycle status object with label, value, and asset_page_label
 */
export function calculateLifecycleStatus(asset, currentTime = moment().unix()) {
    if (!asset.lifecycle_expiry_date) {
        return { asset_page_label: '' };
    }

    const lifecycleDueDate = moment.utc(asset.lifecycle_expiry_date.seconds * 1000);
    const currentDay = moment.utc(currentTime * 1000);
    const lifecycleDaysLeft = lifecycleDueDate.diff(currentDay, 'days') + 1;
    const lifecycleWarningValue = asset.lifecycle_warning && asset.lifecycle_warning.value
        ? asset.lifecycle_warning.value
        : 0;

    if (currentTime > lifecycleDueDate.unix()) {
        return {
            label: 'Lifecycle Expired',
            value: '#f44336',
            asset_page_label: 'Expired'
        };
    } else if ((lifecycleDueDate.unix() - lifecycleWarningValue) < currentTime) {
        return {
            label: `Lifecycle Expires in ${lifecycleDaysLeft} day(s).`,
            value: '#ffbf00',
            asset_page_label: 'Warning'
        };
    } else {
        return {
            label: `Lifecycle Expires in ${lifecycleDaysLeft} day(s).`,
            value: '#439446',
            asset_page_label: 'OK'
        };
    }
}
